import {
  ConfigResponse,
  PagedModelConfigResponse,
  PublistConfigApi,
  PubListConfigListTypeEnum,
  PublistListApi,
  PublistShareApi,
  SearchConfigsByUserIdNameSortDirEnum,
} from '../../../api/core';
import { getAuthMiddleware } from '../../../Auth/utils/GetAuthMiddleware';
import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Loading } from '../../system/Loading';
import { defaultRowsPerPage, pageSizeOptions, searchDelayDefault } from '../../../Settings/config';
import { UsersDTO } from '../../../api/admin';
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowToggleEvent,
  DataTableSelectionMultipleChangeEvent,
  DataTableStateEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { IntlShape, useIntl } from 'react-intl';
import { AvatarGroup } from 'primereact/avatargroup';
import { Avatar } from 'primereact/avatar';
import { UserAvatarComponent } from '../../components/UserAvatar';
import { Button } from 'primereact/button';
import { UserChipComponent } from '../UserChip';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { NavigateFunction, useNavigate } from 'react-router';
import { ToastContext, ToastContextType } from '../../../util/providers/ToastProvider';
import { EmbeddingDialog } from './EmbeddingDialog';
import { ShareDialog } from './ShareDialog';
import { DataView, DataViewPageEvent } from 'primereact/dataview';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';

export interface ExtendedConfigResponse extends ConfigResponse {
  shareUsers: {
    owner: UsersDTO;
    sharedBy: UsersDTO;
    sharedByHierarchy: UsersDTO[];
    sharedWith: UsersDTO[];
    sharedWithHierarchy: UsersDTO[][];
  };
}


const initialLazyTableState: DataTablePageEvent = {
  first: 0,
  rows: defaultRowsPerPage,
  page: 0,
  sortField: 'config.name',
  sortOrder: 1,
  filters: {},
};

export const Publists = (): ReactNode => {
  const intl: IntlShape = useIntl();
  const toast: ToastContextType = useContext(ToastContext);
  const {actions, loadUsersByIds} = useContext(AuthContext);
  const navigate: NavigateFunction = useNavigate();
  const configServiceApi: PublistConfigApi = new PublistConfigApi().withMiddleware(getAuthMiddleware(actions));
  const shareServiceApi: PublistShareApi = new PublistShareApi().withMiddleware(getAuthMiddleware(actions));
  const publistServiceApi: PublistListApi = new PublistListApi().withMiddleware(getAuthMiddleware(actions));
  const [configs, setConfigs] = useState<ExtendedConfigResponse[]>(null);
  const [selected, setSelected] = useState<ExtendedConfigResponse[]>([]);
  const [expanded, setExpanded] = useState<any>([]);
  const [shared, setShared] = useState<ExtendedConfigResponse[]>([]);

  const [lazyState, setLazyState] = useState<DataTablePageEvent>(initialLazyTableState);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [embed, setEmbed] = useState<string>(null);

  const [totalElements, setTotalElements] = useState<number>(null);
  const [loading, setLoading] = useState(false);

  useEffect((): () => void => {
    const delayDebounceFn = setTimeout((): void => loadConfig(), searchDelayDefault);
    return (): void => clearTimeout(delayDebounceFn);
  }, [lazyState, searchTerm]);

  useEffect((): void => {
    if (shared && shared.length > 0) {
      setShared(state =>
        state
          .map(s => configs.find(c => c.apiKey === s.apiKey))
          .filter(s => s !== undefined),
      );
    }
  }, [configs]);

  const preparePublist = async (publist: ConfigResponse): Promise<ExtendedConfigResponse> => {
    const sharedWithHierarchy: number[][] = publist.share.sharedWith ? await shareServiceApi.getUsersSharedWithHierarchy({apiKey: publist.apiKey}).catch(() => []) : [];
    const sharedByHierarchy: number[] = publist.share.sharedBy ? await shareServiceApi.getUsersSharedByHierarchy({apiKey: publist.apiKey}).catch(() => []) : [];
    const userIds: number[] = [...new Set([...sharedWithHierarchy.flat(), ...sharedByHierarchy, publist.share.ownerId, publist.share.sharedBy, ...(publist.share.sharedWith || [])].filter(Boolean))];
    const users: { [key: string]: UsersDTO } = await loadUsersByIds(userIds).catch(() => ({}));
    return {
      ...publist,
      shareUsers: {
        owner: users[publist.share.ownerId] || undefined,
        sharedBy: users[publist.share.sharedBy] || undefined,
        sharedByHierarchy: (sharedByHierarchy || []).map((id: number): UsersDTO => users[id] || undefined),
        sharedWith: (publist.share.sharedWith || []).map((id: number): UsersDTO => users[id] || undefined),
        sharedWithHierarchy: (sharedWithHierarchy || []).map((hierarchy: number[]): UsersDTO[] => (hierarchy || []).map((id: number): UsersDTO => users[id] || undefined)),
      },
    };
  };

  const onPageChange = (e: DataTableStateEvent | DataViewPageEvent): void => setLazyState(e);

  const onSortChange = (e: DataTableStateEvent): void => setLazyState(e);

  const onFilterChange = (e: DataTableStateEvent): void => setLazyState({...e, first: 0});

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => setSearchTerm(e.target.value);

  const deleteList = (data: { apiKey: string; configName?: string }): void => {
    toast.confirm({
      message: <div className={'flex flex-column gap-2'}>
        <p>{intl.formatMessage({id: 'user.profile.publist.popup.delete.single.message'})}</p>
        <div
          className={'flex gap-1 flex-wrap'}>{data.configName || intl.formatMessage({id: 'publist.unnamedPublist'})} ({data.apiKey})
        </div>
        ,
      </div>,
      header: intl.formatMessage({id: 'user.profile.publist.popup.delete.headline'}),
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: intl.formatMessage({id: 'button.confirm'}),
      accept: async () => {
        try {
          await publistServiceApi.deletePublicationList({apiKey: data.apiKey});
          toast.notify({
            summary: intl.formatMessage({id: 'message.deletion.success.title'}),
            detail: intl.formatMessage({id: 'message.deletion.success.message'}),
            severity: 'success',
          });
          loadConfig();
        } catch (e) {
          toast.error(e, 'message.deletion.failed');
        }
      },
      rejectLabel: intl.formatMessage({id: 'button.abort'}),
    });
  };

  const deleteLists = (data: { apiKey: string; configName?: string }[]): void => {
    toast.confirm({
      message: <div className={'flex flex-column gap-2'}>
        <p>{intl.formatMessage({id: 'user.profile.publist.popup.delete.multiple.message'})}</p>
        {data.map((row, index: number): ReactNode =>
          <div key={index}
               className={'flex gap-1 flex-wrap'}>{row.configName || intl.formatMessage({id: 'publist.unnamedPublist'})} ({row.apiKey})</div>,
        )}
      </div>,
      header: intl.formatMessage({id: 'user.profile.publist.popup.delete.headline'}),
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      acceptLabel: intl.formatMessage({id: 'button.confirm'}),
      accept: async (): Promise<void> => {
        try {
          await publistServiceApi.deletePublicationLists({apiKeys: data.map((entry): string => entry.apiKey)});
          setSelected([]);
          toast.notify({
            summary: intl.formatMessage({id: 'message.deletion.success.title'}),
            detail: intl.formatMessage({id: 'message.deletion.success.message'}),
            severity: 'success',
          });
          loadConfig();
        } catch (e) {
          toast.error(e, 'message.deletion.failed');
        }

      },
      rejectLabel: intl.formatMessage({id: 'button.abort'}),
    });
  };

  const loadConfig = (): void => {
    let nameSortDir: SearchConfigsByUserIdNameSortDirEnum | undefined;
    switch (true) {
      case lazyState.sortOrder < 0:
        nameSortDir = SearchConfigsByUserIdNameSortDirEnum.Desc;
        break;
      case lazyState.sortOrder > 0:
      default:
        nameSortDir = SearchConfigsByUserIdNameSortDirEnum.Asc;
        break;
    }
    void configServiceApi.searchConfigsByUserId({
      searchTerm,
      nameSortDir,
      page: lazyState.page,
      size: lazyState.rows,
    })
      .then((result: PagedModelConfigResponse): void => {
        setTotalElements(result.page.totalElements);
        Promise.all(result.content.map((row: ConfigResponse): Promise<ExtendedConfigResponse> => preparePublist(row)))
          .then((extended: ExtendedConfigResponse[]): void => setConfigs(extended || []))
          .catch((error: PagedModelConfigResponse): void => {
            toast.error(error, 'message.loading.failed');
          })
          .finally((): void => setLoading((): boolean => false));
      });
  };

  if (!configs && !loading) {
    setLoading(true);
    loadConfig();
  }
  if (!configs || loading) return <Loading/>;

  const template = (data: ExtendedConfigResponse): ReactNode => {
    return <div className={'flex flex-column gap-2'}>
      {data.shareUsers?.sharedByHierarchy && data.shareUsers.sharedByHierarchy.length > 0 &&
        <div className={'flex flex-column gap-1'}>
          <h3 className={'mt-0'}>{intl.formatMessage({id: 'publist.col.sharedBy'})}</h3>
          <div className={'flex gap-1'}>
            {data.shareUsers.sharedByHierarchy.map((user: UsersDTO, index: number, array: UsersDTO[]): ReactNode =>
              <>
                <UserChipComponent user={user} key={index}/>
                {index + 1 < array.length && ' > '}
              </>,
            )}
          </div>
        </div>}
      {data.shareUsers?.sharedWithHierarchy && data.shareUsers.sharedWithHierarchy.length > 0 &&
        <div className={'flex flex-column gap-1'}>
          <h3 className={'mt-0'}>{intl.formatMessage({id: 'publist.col.sharedWith'})}</h3>
          {data.shareUsers.sharedWithHierarchy.map((users: UsersDTO[]): ReactNode =>
            <div className={'flex gap-1'}>
              {users.map((user: UsersDTO, index: number, array: UsersDTO[]): ReactNode =>
                <>
                  <UserChipComponent user={user} key={index}/>
                  {index + 1 < array.length && ' > '}
                </>,
              )}
            </div>,
          )}
        </div>}
    </div>;
  };

  const header = (): ReactNode => {
    return (
      <div className={'flex flex-column gap-2 w-full'}>
        <div className={'flex gap-2 justify-content-between'}>
          <div className={'flex lg:flex-row flex-column gap-3'}>
            <EmbeddingDialog
              visible={!!embed}
              setVisible={(): void => setEmbed(null)}
              data={JSON.parse(embed) as { key: string; name?: string }}
            />
            <ShareDialog
              visible={!!shared.length}
              updateData={(): void => loadConfig()}
              data={shared}
              onHide={(): void => setShared([])}
            />
            <Button
              className={'hidden sm:block'}
              onClick={(): void =>
                deleteLists(
                  selected.map((row: ExtendedConfigResponse) => ({
                    apiKey: row.apiKey,
                    configName: row.config.name,
                  }))
                )
              }
              disabled={selected.length === 0}
              icon={'pi pi-trash'}
              severity={'danger'}
              label={intl.formatMessage({ id: 'publist.deleteSelected' })}
            />
            <Button
              className={'sm:hidden block'}
              onClick={(): void =>
                deleteLists(
                  selected.map((row: ExtendedConfigResponse) => ({
                    apiKey: row.apiKey,
                    configName: row.config.name,
                  }))
                )
              }
              disabled={selected.length === 0}
              icon={'pi pi-trash'}
              severity={'danger'}
              tooltip={intl.formatMessage({ id: 'publist.deleteSelected' })}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              className={'hidden sm:block'}
              onClick={(): void => setShared(selected)}
              disabled={selected.length === 0}
              icon={'pi pi-share-alt'}
              label={intl.formatMessage({ id: 'publist.shareSelected' })}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              className={'sm:hidden block'}
              onClick={(): void => setShared(selected)}
              disabled={selected.length === 0}
              icon={'pi pi-share-alt'}
              tooltip={intl.formatMessage({ id: 'publist.shareSelected' })}
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>
          <div className={'flex lg:flex-row flex-column gap-3'}>
            <div className={'flex max-w-15rem w-full p-fluid'}>
              <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText
                  value={searchTerm}
                  onChange={onGlobalFilterChange}
                  placeholder={intl.formatMessage({ id: 'search.placeholder' })}
                />
              </IconField>
            </div>
            <div className={'flex max-w-15rem w-full p-fluid'}>
              <Dropdown
                value={lazyState.sortOrder}
                options={[
                  { value: 1, label: intl.formatMessage({ id: 'publist.sort.name.asc' }) },
                  { value: -1, label: intl.formatMessage({ id: 'publist.sort.name.desc' }) },
                ]}
                onChange={(event: DropdownChangeEvent): void =>
                  setLazyState((lz) => ({ ...lz, sortOrder: event.value }))
                }
                optionLabel="label"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return <div>
    <div className={'block md:hidden'}>
      <DataView
        dataKey={'apiKey'}
        emptyMessage={intl.formatMessage({id: 'publists.empty'})}
        header={header()}
        value={configs}
        layout={'list'}
        loading={loading}
        lazy={true}
        paginator={true}
        totalRecords={totalElements}
        rowsPerPageOptions={pageSizeOptions}
        first={lazyState.first}
        rows={lazyState.rows}
        onPage={onPageChange}
        listTemplate={(listConfigs: ExtendedConfigResponse[]) =>
          <div className="flex flex-column w-full">
            {listConfigs.map((config: ExtendedConfigResponse): ReactNode =>
              <div className={'p-3 flex gap-1'}>
                <div className={'flex justify-content-center align-content-center w-2'}>
                  <Checkbox
                    checked={selected.map(s => s.apiKey).includes(config.apiKey)}
                    onChange={(event: CheckboxChangeEvent): void => {
                      if (event.checked) {
                        if (!selected.map(s => s.apiKey).includes(config.apiKey)) {
                          setSelected([...selected, config]);
                        }
                      } else {
                        setSelected(selected.filter(s => s.apiKey !== config.apiKey));
                      }
                    }}
                  />
                </div>
                <div className={' flex flex-column gap-1 w-10'}>
                  <div className={'text-xs'} title={''}>
                    {config.apiKey}
                  </div>
                  <strong>{config.config.name ||
                    <span className={'text-300'}>{intl.formatMessage({id: 'publist.unnamedPublist'})}</span>}</strong>
                  <small>{intl.formatMessage({id: `publist.col.type.${config.config.listType.toLowerCase()}`})}</small>
                  {config.shareUsers.sharedBy && <small>
                    {intl.formatMessage({id: 'publists.col.sharedBy'})}:
                    {config.shareUsers.sharedBy?.givenname} {config.shareUsers.sharedBy?.surname}</small>}
                  {config.shareUsers.sharedWith && config.shareUsers.sharedWith.length > 0 && <small>
                    {intl.formatMessage({id: 'publists.col.sharedWith'})}:
                    {config.shareUsers.sharedWith
                      .filter((u: UsersDTO): boolean => !!u)
                      .map((u: UsersDTO): string => `${u?.givenname} ${u?.surname}`).join(', ')}
                  </small>}
                  <div className={'flex gap-2 justify-content-end flex-wrap'}>
                    <Button icon={'pi pi-share-alt'}
                            onClick={(): void => setShared([config])}
                            size={'small'}
                            tooltip={intl.formatMessage({id: 'publist.button.share'})}
                            tooltipOptions={{position: 'bottom'}}
                    />
                    <Button icon={'pi pi-pencil'}
                            onClick={(): void => void navigate(`/publist/generator/${config.config.listType === PubListConfigListTypeEnum.ResearchData ? 'research' : 'publications'}}/${config.apiKey}`)}
                            size={'small'}
                            tooltip={intl.formatMessage({id: 'publist.button.edit'})}
                            tooltipOptions={{position: 'bottom'}}
                    />
                    <Button icon={'pi pi-code'}
                            onClick={(): void => setEmbed(JSON.stringify({
                              key: config.apiKey,
                              name: config.config.name,
                            }))} size={'small'}
                            tooltip={intl.formatMessage({id: 'publist.button.embed'})}
                            tooltipOptions={{position: 'bottom'}}
                    />
                    <Button icon={'pi pi-trash'} severity={'danger'}
                            onClick={(): void => deleteList({apiKey: config.apiKey, configName: config.config.name})}
                            size={'small'}
                            tooltip={intl.formatMessage({id: 'publist.button.delete'})}
                            tooltipOptions={{position: 'bottom'}}
                    />
                  </div>
                </div>
              </div>,
            )}
          </div>
        }
      />
    </div>
    <div className={'hidden md:block'}>
      <DataTable
        header={header}
        lazy={true}
        emptyMessage={intl.formatMessage({id: 'publists.empty'})}
        stripedRows={true}
        showGridlines={true}
        dataKey={'apiKey'}
        paginator={true}
        totalRecords={totalElements}
        loading={loading}
        rowsPerPageOptions={pageSizeOptions}
        value={configs}
        first={lazyState.first}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        filters={lazyState.filters}
        scrollable={true}
        rows={lazyState.rows}
        onPage={onPageChange}
        onSort={onSortChange}
        onFilter={onFilterChange}
        selectionMode={'checkbox'}
        selection={selected}
        onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<ExtendedConfigResponse[]>): void => setSelected(e.value)}
        expandedRows={expanded}
        rowExpansionTemplate={template}
        onRowToggle={(e: DataTableRowToggleEvent): void => setExpanded(e.data)}
      >
        <Column expander={(data: ExtendedConfigResponse): boolean =>
          (data.shareUsers?.sharedByHierarchy && data.shareUsers.sharedByHierarchy.length > 0)
          || (data.shareUsers?.sharedWithHierarchy && data.shareUsers.sharedWithHierarchy.length > 0)
        } headerStyle={{width: '5rem'}} style={{width: '5rem'}}/>
        <Column selectionMode="multiple" headerStyle={{width: '3rem'}}></Column>
        <Column field={'apiKey'} header={intl.formatMessage({id: 'publists.col.id'})}/>
        <Column field={'config.name'} header={intl.formatMessage({id: 'publists.col.name'})}
                sortable={true}
                body={(data: ExtendedConfigResponse): ReactNode => <div>{data.config.name ||
                  <span className={'text-300'}>{intl.formatMessage({id: 'publist.unnamedPublist'})}</span>}</div>}/>
        <Column field={'config.listType'} header={intl.formatMessage({id: 'publists.col.type'})}
                body={(data: ExtendedConfigResponse): ReactNode =>
                  <div>{intl.formatMessage({id: `publist.col.type.${data.config.listType.toLowerCase()}`})}</div>}/>
        <Column header={intl.formatMessage({id: 'publists.col.sharedWith'})}
                body={(data: ExtendedConfigResponse): ReactNode => <AvatarGroup>
                  {data.shareUsers.sharedWith.map((user: UsersDTO, index: number, array: UsersDTO[]): ReactNode => {
                    if (array.length < 6 || index < 5) {
                      return <UserAvatarComponent type={'small'} key={index} user={user}/>;
                    }
                    if (index === 5) {
                      return <Avatar shape="circle" key={index} label={`+${array.length - 4}`}/>;
                    }
                    return <></>;
                  })}
                </AvatarGroup>}/>
        <Column header={intl.formatMessage({id: 'publists.col.sharedBy'})}
                body={(data: ExtendedConfigResponse): ReactNode => {
                  if (!data.shareUsers.sharedBy) {
                    return <></>;
                  }
                  return <UserAvatarComponent type={'small'} user={data.shareUsers.sharedBy}/>;
                }}/>
        <Column body={(data: ExtendedConfigResponse): ReactNode => {
          return (
            <div className={'flex gap-2'}>
              <Button
                icon={'pi pi-share-alt'}
                tooltip={intl.formatMessage({ id: 'publist.button.share' })}
                tooltipOptions={{ position: 'bottom' }}
                onClick={(): void => setShared([data])}
              />
              <Button
                icon={'pi pi-pencil'}
                tooltip={intl.formatMessage({ id: 'publist.button.edit' })}
                tooltipOptions={{ position: 'bottom' }}
                onClick={(): void =>
                  void navigate(
                    `/publist/generator/${data.config.listType === PubListConfigListTypeEnum.ResearchData ? 'research' : 'publications'}/${data.apiKey}`
                  )
                }
              />
              <Button
                icon={'pi pi-code'}
                tooltip={intl.formatMessage({ id: 'publist.button.embed' })}
                tooltipOptions={{ position: 'bottom' }}
                onClick={(): void => setEmbed(JSON.stringify({ key: data.apiKey, name: data.config.name }))}
              />
              <Button
                icon={'pi pi-trash'}
                severity={'danger'}
                tooltip={intl.formatMessage({ id: 'publist.button.delete' })}
                tooltipOptions={{ position: 'bottom' }}
                onClick={(): void => deleteList({ apiKey: data.apiKey, configName: data.config.name })}
              />
            </div>
          );
        }}/>
      </DataTable>
    </div>
  </div>;
};
